import React, { useEffect, useState } from "react";
import { Inbox as InboxIcon, Plus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

export default function Inbox() {
  const [rows, setRows] = useState([]);
  const [domains, setDomains] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ domain_id: "", from_addr: "", to_addr: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/emails", { params: { direction: "inbound", limit: 200 } }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/domains").then((r) => { setDomains(r.data); if (r.data[0]) setForm((f) => ({ ...f, domain_id: f.domain_id || r.data[0].id, to_addr: f.to_addr || `support@${r.data[0].domain}` })); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const simulate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inbound/test", form);
      toast.success("Inbound message received");
      setShowForm(false);
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="display text-2xl font-bold">Inbound Mailbox</h1>
          <p className="mt-1 text-sm text-slate-400">Email received at your verified domains.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="inbound-simulate-toggle" className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 hover:border-emerald-500/50">
          <Plus className="h-4 w-4" /> Simulate inbound
        </button>
      </div>

      {showForm && (
        <form onSubmit={simulate} className="grid gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4 sm:grid-cols-2" data-testid="inbound-form">
          <select value={form.domain_id} onChange={(e) => setForm({ ...form, domain_id: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm">
            {domains.map((d) => <option key={d.id} value={d.id}>{d.domain}</option>)}
          </select>
          <input value={form.to_addr} onChange={(e) => setForm({ ...form, to_addr: e.target.value })} placeholder="to (support@yourdomain)" className="rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm" />
          <input value={form.from_addr} onChange={(e) => setForm({ ...form, from_addr: e.target.value })} placeholder="from (customer@example.com)" className="rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm" required />
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="subject" className="rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm" required />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="message body" className="sm:col-span-2 rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm" rows={3} />
          <button disabled={busy} data-testid="inbound-submit" className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2.5 text-sm font-semibold text-[#04120c] hover:bg-emerald-400 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <InboxIcon className="h-4 w-4" />} Receive message
          </button>
        </form>
      )}

      <div className="divide-y divide-slate-800 overflow-hidden rounded-xl border border-slate-800 bg-slate-900/40">
        {rows.length === 0 && <div className="py-12 text-center text-sm text-slate-500">No inbound mail yet.</div>}
        {rows.map((e) => (
          <div key={e.id} data-testid="inbox-row" onClick={() => setSelected(e)} className="flex cursor-pointer items-center gap-4 px-5 py-4 transition-colors hover:bg-slate-800/40">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-500/10 text-xs font-semibold text-cyan-400">{(e.from_addr || "?")[0].toUpperCase()}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="mono truncate text-sm text-slate-300">{e.from_addr}</span>
                <span className="shrink-0 text-xs text-slate-500">{new Date(e.created_at).toLocaleDateString()}</span>
              </div>
              <div className="truncate font-medium">{e.subject}</div>
              <div className="mono truncate text-xs text-slate-500">to {e.to_addrs?.join(", ")}</div>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={() => setSelected(null)}>
          <div className="h-full w-full max-w-md overflow-y-auto border-l border-slate-800 bg-[#0B0F17] p-6" onClick={(e) => e.stopPropagation()} data-testid="inbox-detail">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="display text-lg font-semibold">{selected.subject}</h2>
              <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="mono space-y-1 text-xs text-slate-400">
              <div>From: {selected.from_addr}</div>
              <div>To: {selected.to_addrs?.join(", ")}</div>
              <div>Received: {new Date(selected.created_at).toLocaleString()}</div>
            </div>
            <div className="mt-4 whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950 p-4 text-sm text-slate-300">{selected.text || "(no body)"}</div>
          </div>
        </div>
      )}
    </div>
  );
}
